import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Radio, X, Youtube, Facebook, Twitch, Monitor, 
  Copy, Check, AlertCircle, Settings2, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const STREAM_PRESETS = [
  { id: 'youtube', name: 'YouTube Live', icon: Youtube, rtmpUrl: 'rtmp://a.rtmp.youtube.com/live2' },
  { id: 'facebook', name: 'Facebook Live', icon: Facebook, rtmpUrl: 'rtmps://live-api-s.facebook.com:443/rtmp/' },
  { id: 'twitch', name: 'Twitch', icon: Twitch, rtmpUrl: 'rtmp://live.twitch.tv/app' },
  { id: 'custom', name: 'Custom RTMP', icon: Monitor, rtmpUrl: '' },
];

const StreamSettings = ({ 
  open, 
  onClose,
  projectId,
  onStreamStart,
  onStreamStop,
  isStreaming
}) => {
  const [selectedPreset, setSelectedPreset] = useState('youtube');
  const [rtmpUrl, setRtmpUrl] = useState(STREAM_PRESETS[0].rtmpUrl);
  const [streamKey, setStreamKey] = useState('');
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [resolution, setResolution] = useState('1080p');
  const [bitrate, setBitrate] = useState('4500');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePresetChange = (presetId) => {
    setSelectedPreset(presetId);
    const preset = STREAM_PRESETS.find(p => p.id === presetId);
    if (preset && preset.rtmpUrl) {
      setRtmpUrl(preset.rtmpUrl);
    }
  };

  const handleStartStream = () => {
    if (!streamKey.trim()) {
      toast.error('Please enter your stream key');
      return;
    }

    const fullRtmpUrl = `${rtmpUrl}/${streamKey}`;
    
    // Store stream config
    const streamConfig = {
      preset: selectedPreset,
      rtmpUrl: fullRtmpUrl,
      resolution,
      bitrate: parseInt(bitrate),
      projectId
    };

    // In a real implementation, this would start an RTMP stream
    // For now, we'll simulate the streaming state
    localStorage.setItem(`stream-config-${projectId}`, JSON.stringify(streamConfig));
    
    onStreamStart?.(streamConfig);
    toast.success('Stream started! Your presentation is now live.');
  };

  const handleStopStream = () => {
    localStorage.removeItem(`stream-config-${projectId}`);
    onStreamStop?.();
    toast.success('Stream stopped');
  };

  const copyStreamUrl = () => {
    const fullUrl = `${rtmpUrl}/${streamKey}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Stream URL copied!');
  };

  const getInstructions = () => {
    switch (selectedPreset) {
      case 'youtube':
        return (
          <div className="text-xs text-slate-400 space-y-1">
            <p>1. Go to YouTube Studio → Go Live → Stream</p>
            <p>2. Copy your Stream key from the stream settings</p>
            <p>3. Paste it above and click Start Stream</p>
          </div>
        );
      case 'facebook':
        return (
          <div className="text-xs text-slate-400 space-y-1">
            <p>1. Go to Facebook → Live Video → Use Stream Key</p>
            <p>2. Copy your Stream key</p>
            <p>3. Paste it above and click Start Stream</p>
          </div>
        );
      case 'twitch':
        return (
          <div className="text-xs text-slate-400 space-y-1">
            <p>1. Go to Twitch Dashboard → Settings → Stream</p>
            <p>2. Copy your Primary Stream Key</p>
            <p>3. Paste it above and click Start Stream</p>
          </div>
        );
      default:
        return (
          <div className="text-xs text-slate-400">
            <p>Enter your custom RTMP URL and stream key</p>
          </div>
        );
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-slate-900 rounded-2xl w-full max-w-lg border border-slate-700 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isStreaming ? 'bg-red-500/20' : 'bg-amber-500/20'
              }`}>
                <Radio className={`w-5 h-5 ${isStreaming ? 'text-red-400 animate-pulse' : 'text-amber-400'}`} />
              </div>
              <div>
                <h2 className="font-semibold text-white">Live Stream Settings</h2>
                <p className="text-xs text-slate-400">
                  {isStreaming ? 'Currently streaming live' : 'Configure your stream'}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {isStreaming ? (
              /* Streaming Active UI */
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 py-6">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-400 font-medium">LIVE</span>
                </div>
                
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-sm text-slate-300 text-center">
                    Your presentation is being streamed to {STREAM_PRESETS.find(p => p.id === selectedPreset)?.name || 'your platform'}
                  </p>
                </div>

                <Button 
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleStopStream}
                >
                  Stop Streaming
                </Button>
              </div>
            ) : (
              /* Setup UI */
              <>
                {/* Platform Selection */}
                <div className="space-y-2">
                  <Label>Streaming Platform</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {STREAM_PRESETS.map((preset) => {
                      const Icon = preset.icon;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => handlePresetChange(preset.id)}
                          className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                            selectedPreset === preset.id
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${
                            selectedPreset === preset.id ? 'text-amber-400' : 'text-slate-400'
                          }`} />
                          <span className="text-xs text-slate-300">{preset.name.split(' ')[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* RTMP URL (for custom) */}
                {selectedPreset === 'custom' && (
                  <div className="space-y-2">
                    <Label>RTMP Server URL</Label>
                    <Input
                      placeholder="rtmp://your-server.com/live"
                      value={rtmpUrl}
                      onChange={(e) => setRtmpUrl(e.target.value)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                )}

                {/* Stream Key */}
                <div className="space-y-2">
                  <Label>Stream Key</Label>
                  <div className="relative">
                    <Input
                      type={showStreamKey ? 'text' : 'password'}
                      placeholder="Paste your stream key here"
                      value={streamKey}
                      onChange={(e) => setStreamKey(e.target.value)}
                      className="bg-slate-800 border-slate-700 pr-20"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8"
                        onClick={() => setShowStreamKey(!showStreamKey)}
                      >
                        {showStreamKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  {getInstructions()}
                </div>

                {/* Advanced Settings */}
                <div className="space-y-3">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    <Settings2 className="w-4 h-4" />
                    Advanced Settings
                  </button>

                  {showAdvanced && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-3 pt-2"
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Resolution</Label>
                          <Select value={resolution} onValueChange={setResolution}>
                            <SelectTrigger className="bg-slate-800 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="720p">720p (HD)</SelectItem>
                              <SelectItem value="1080p">1080p (Full HD)</SelectItem>
                              <SelectItem value="1440p">1440p (2K)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Bitrate (kbps)</Label>
                          <Select value={bitrate} onValueChange={setBitrate}>
                            <SelectTrigger className="bg-slate-800 border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="2500">2500 (Low)</SelectItem>
                              <SelectItem value="4500">4500 (Medium)</SelectItem>
                              <SelectItem value="6000">6000 (High)</SelectItem>
                              <SelectItem value="8000">8000 (Ultra)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Warning */}
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200">
                    Make sure your streaming platform is ready to receive the stream before clicking Start.
                  </p>
                </div>

                {/* Start Button */}
                <Button 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                  onClick={handleStartStream}
                  disabled={!streamKey.trim()}
                >
                  <Radio className="w-4 h-4 mr-2" />
                  Start Streaming
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StreamSettings;
